// Alert — inline status message with intent color, icon, optional title.
// Color is never the only signal: each intent ships a default icon.

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import Icon from './Icon';

export type AlertVariant = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: AlertVariant;
  /** Bold heading above the body. */
  title?: ReactNode;
  /** Override the default per-variant icon. Kebab-case Icon name. */
  icon?: string;
}

const DEFAULT_ICON: Record<AlertVariant, string> = {
  success: 'circle-check',
  info: 'info',
  warning: 'triangle-alert',
  danger: 'circle-alert',
  // A quiet aside rather than an intent — same ⓘ, no color claim.
  neutral: 'info',
};

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { variant = 'info', title, icon, className = '', children, ...rest },
  ref,
) {
  const classes = ['app-alert', `app-alert--${variant}`, className].filter(Boolean).join(' ');

  return (
    <div ref={ref} role='alert' className={classes} {...rest}>
      <Icon
        name={icon ?? DEFAULT_ICON[variant]}
        size={18}
        className='app-alert__icon'
        aria-hidden='true'
      />
      <div className='app-alert__body'>
        {title && <b className='app-alert__title'>{title}</b>}
        {children && <p className='app-alert__text'>{children}</p>}
      </div>
    </div>
  );
});

export default Alert;
